import { Tab } from '@scuf/common';
import { useTranslation } from 'react-i18next';
import { COMPANY_MANAGER, USER_MANAGER, HSL, notificationAtom } from 'resources';
import { useSetRecoilState } from 'recoil';
import { hslCountriesAtom, hslPartnersAtom, hslPartsAtom } from 'resources/atoms/hslAtom';
import { useEffect, useState } from 'react';
import { CompanyDTO } from 'models/userManager/addCustomer';
import ProductSearch from './products/productSearch';
import Report from './report/report';
import InventorySearch from './inventory/inventorySearch';
import ContractSearch from './contracts/contractSearch';
import RepairSearch from './repair/repairSearch';
import QuotationSearch from './quotation/quotationSearch';
import useUser from 'helpers/hooks/useUser';
import useFetch from 'helpers/hooks/useFetch';
import HolidaySearch from './holiday/holidaySearch';

const SUPERADMIN = '1';
const ADMIN = '2';

interface ManualFile {
    id: number;
    displayName: string;
    storedFileName: string;
    section: 'HSL';
}

function HslPage() {
    const { t } = useTranslation(['hsl']);
    const setNotification = useSetRecoilState(notificationAtom);
    const setPartners = useSetRecoilState(hslPartnersAtom);
    const setCountries = useSetRecoilState(hslCountriesAtom);
    const setParts = useSetRecoilState(hslPartsAtom);
    const { user: userDetails, getUser } = useUser();

    const [show, setShow] = useState(false);
    const [fileUrl, setFileUrl] = useState<string>();
    const [, fetchFiles] = useFetch<void, ManualFile[]>({
        url: `${USER_MANAGER.getManualFilesBySection}?section=HSLWEB`,
        method: 'GET'
    });

    useEffect(() => {
        if (userDetails && Object.keys(userDetails).length > 0) {
            loadFiles();
        }
    }, [userDetails]);

    const loadFiles = async (): Promise<void> => {
        try {
            await fetchFiles(undefined, resp => {
                const checkList: ManualFile[] = resp?.data ?? [];
                if (checkList.length > 0) {
                    const foundFile = checkList.find(file => file.displayName === 'mensagemhsl');
                    if (foundFile) {
                        const url = `${USER_MANAGER.downloadManualFileById}?id=${foundFile.id}`;
                        setFileUrl(url);
                        setShow(true);
                    }
                }
            });
        } catch (error: any) {
            if (error.status === 401) {
                await getUser();
            } else {
                console.error(t('manual.fetchFilesError'));
            }
        }
    };

    const [, getPartners] = useFetch({
        url: COMPANY_MANAGER.getHslPartners,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    const [, getParts] = useFetch({
        url: HSL.getParts,
        method: 'GET',
        onError: t('common:errors.Unexpected')
    });

    useEffect(() => {
        getAllPartners();
        getAllParts();
    }, []);

    const getAllPartners = () => {
        getPartners(null, resp => {
            if (resp.data) {
                const partnersData = resp.data.map((partner: CompanyDTO) => ({
                    value: partner.name,
                    text: partner.name
                }));
                setPartners(partnersData);

                const uniqueCountries = Array.from(new Set(resp.data.map((partner: CompanyDTO) => String(partner.country))));
                const countriesData = uniqueCountries.map(country => ({
                    value: country as string,
                    text: country as string
                }));
                setCountries(countriesData);
            }
        });
    };

    const getAllParts = () => {
        getParts(null, (current: any) => {
            if (current.data.length) {
                setParts(current.data);
            } else {
                setNotification(old => ({ ...old, type: 'information', message: t('hsl:translation.NotFoundParts') }));
            }
        });
    };

    return (
        <>
            {show && userDetails && Object.keys(userDetails).length > 0 && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999
                    }}
                    onClick={() => setShow(false)}
                >
                    <img
                        style={{ maxWidth: '800px', maxHeight: '800px' }}
                        src={fileUrl}
                        onClick={e => e.stopPropagation()}
                        title="Mensagem Portal"
                    />
                </div>
            )}
            <div className="container">
                <Tab className="tab-with-divider">
                    <Tab.Pane title={t('translation.Repair')}>
                        <RepairSearch />
                    </Tab.Pane>
                    <Tab.Pane title={t('translation.Contracts')}>
                        <ContractSearch />
                    </Tab.Pane>
                    <Tab.Pane title={t('translation.Inventory')}>
                        <InventorySearch />
                    </Tab.Pane>
                    {userDetails?.role === SUPERADMIN || userDetails?.role === ADMIN ? (
                        <Tab.Pane title={t('translation.ProductManagement')}>
                            <ProductSearch />
                        </Tab.Pane>
                    ) : (
                        []
                    )}
                    <Tab.Pane title={t('translation.Quotation')}>
                        <QuotationSearch />
                    </Tab.Pane>
                    {userDetails?.role === SUPERADMIN || userDetails?.role === ADMIN ? (
                        <Tab.Pane title={t('translation.Report')}>
                            <Report />
                        </Tab.Pane>
                    ) : (
                        []
                    )}
                    {userDetails?.role === SUPERADMIN || userDetails?.role === ADMIN ? (
                        <Tab.Pane title={t('translation.HolidayManagement')}>
                            <HolidaySearch />
                        </Tab.Pane>
                    ) : (
                        []
                    )}
                </Tab>
            </div>
        </>
    );
}

export default HslPage;
